/**
 * Thin top-of-viewport scroll progress for Adventure Prints.
 * Hidden until the user starts scrolling, then follows the theme scroll container
 * (.page-wrapper on desktop, document on mobile).
 *
 * Size changes are only watched once the page has loaded, and are debounced: the page
 * resizes many times while it loads, and every update reads scrollHeight (forcing layout),
 * which showed up as long tasks during load.
 */
import {
  getScrollContainer,
  getScrollEventTarget,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

const track = document.getElementById('scroll-progress');
const bar = document.getElementById('scroll-progress-bar');
if (!track || !bar) {
  /* no-op on layouts without the indicator */
} else {
  const SHOW_AFTER_PX = 8;
  const RESIZE_SETTLE_MS = 150;
  let scrollTarget = null;
  let ticking = false;
  let resizeTimer;

  function setProgress(ratio, scrollTop) {
    const value = Math.min(1, Math.max(0, ratio));
    bar.style.setProperty('--scroll-progress', `${value * 100}%`);
    track.classList.toggle('is-active', scrollTop > SHOW_AFTER_PX);
  }

  function update() {
    const container = getScrollContainer();
    if (!container) {
      setProgress(0, 0);
      return;
    }

    const max = container.scrollHeight - container.clientHeight;
    if (max <= 0) {
      setProgress(0, 0);
      return;
    }

    setProgress(container.scrollTop / max, container.scrollTop);
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      update();
      ticking = false;
    });
  }

  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(update, RESIZE_SETTLE_MS);
  }

  function bindScrollTarget() {
    if (scrollTarget) {
      scrollTarget.removeEventListener('scroll', onScroll);
    }
    scrollTarget = getScrollEventTarget();
    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
    update();
  }

  const resizeObserver = new ResizeObserver(onResize);

  function observeSizes() {
    resizeObserver.observe(document.body);
    const pageWrapper = document.querySelector('.page-wrapper');
    if (pageWrapper) resizeObserver.observe(pageWrapper);
  }

  bindScrollTarget();
  scrollContainerMediaQuery.addEventListener('change', bindScrollTarget);

  if (document.readyState === 'complete') {
    observeSizes();
  } else {
    window.addEventListener(
      'load',
      () => {
        update();
        observeSizes();
      },
      { once: true }
    );
  }

  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('pageshow', update);
  document.addEventListener('shopify:section:load', update);
}
